// components/VerificationOTP.js
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiShield, 
  FiMail, 
  FiPhone, 
  FiCheck, 
  FiX, 
  FiArrowLeft,
  FiRefreshCw,
  FiClock,
  FiUser,
} from 'react-icons/fi';
import API_CONFIG from '../components/API_CONFIG/API_CONFIG';


const VerificationOTP = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userData = location.state || {};
  
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [email, setEmail] = useState(userData.email || '');
  const [tel, setTel] = useState(userData.tel || '');
  const [verificationMethod, setVerificationMethod] = useState('email'); // 'email' or 'sms'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [verifiedUser, setVerifiedUser] = useState(null);
  
  const inputRefs = useRef([]);
  const timerRef = useRef(null);

  // Redirection si pas de données utilisateur
  useEffect(() => {
    if (!userData.email && !userData.tel) {
      navigate('/register');
    }
  }, [userData, navigate]);

  // Auto-focus sur le premier input
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  // Gestion du compte à rebours pour renvoyer le code
  useEffect(() => {
    if (countdown > 0) {
      timerRef.current = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else {
      setResendDisabled(false);
    }
    return () => clearTimeout(timerRef.current);
  }, [countdown]);

  // Envoyer le code OTP
  const sendOTP = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const endpoint = verificationMethod === 'email' ? 'send_otp_email.php' : 'send_otp_sms.php';
      const payload = verificationMethod === 'email' 
        ? { email: email }
        : { tel: tel };

      const response = await fetch(`${API_CONFIG.apiUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(`✅ Code OTP envoyé par ${verificationMethod === 'email' ? 'email' : 'SMS'}`);
        setCountdown(60);
        setResendDisabled(true);
        // Réinitialiser les champs OTP
        setOtp(['', '', '', '', '', '']);
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus();
        }
      } else {
        setError(data.error || 'Erreur lors de l\'envoi du code');
      }
    } catch (error) {
      setError('Erreur de connexion. Veuillez réessayer.');
      console.error('Send OTP error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Vérifier le code OTP
  const verifyOTP = async () => {
    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      setError('Veuillez saisir le code à 6 chiffres');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_CONFIG.apiUrl}verify_otp.php`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          tel: tel,
          otp: otpCode,
          method: verificationMethod
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('✅ Vérification réussie !');
        setVerifiedUser(data.user || { email, tel });
        setShowSuccessModal(true);
        
        // Redirection après 3 secondes
        setTimeout(() => {
          setShowSuccessModal(false);
          navigate('/', { 
            state: { 
              message: 'Compte vérifié avec succès ! Vous pouvez maintenant vous connecter.' 
            } 
          });
        }, 3000);
      } else {
        setError(data.error || 'Code OTP invalide');
        // Secouer les inputs en cas d'erreur
        inputRefs.current.forEach(ref => {
          if (ref) {
            ref.style.animation = 'shake 0.5s ease';
            setTimeout(() => {
              ref.style.animation = '';
            }, 500);
          }
        });
        // Réinitialiser les champs
        setOtp(['', '', '', '', '', '']);
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus();
        }
      }
    } catch (error) {
      setError('Erreur de connexion. Veuillez réessayer.');
      console.error('Verify OTP error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Gestion du changement des inputs OTP
  const handleOtpChange = (index, value) => {
    // Ne garder que les chiffres
    const numericValue = value.replace(/\D/g, '');
    if (numericValue.length > 1) return;

    const newOtp = [...otp];
    newOtp[index] = numericValue;
    setOtp(newOtp);

    // Auto-avancer vers le champ suivant
    if (numericValue && index < 5) {
      inputRefs.current[index + 1].focus();
    }

    // Auto-validation si tous les champs sont remplis
    if (index === 5 && numericValue) {
      const fullOtp = newOtp.join('');
      if (fullOtp.length === 6) {
        setTimeout(() => verifyOTP(), 300);
      }
    }
  };

  // Gestion du clavier (retour arrière)
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
    if (e.key === 'Enter') {
      verifyOTP();
    }
  };

  // Coller le code OTP
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '');
    if (pastedData.length === 6) {
      const newOtp = pastedData.split('');
      setOtp(newOtp);
      inputRefs.current[5].focus();
      // Auto-vérification après collage
      setTimeout(() => verifyOTP(), 500);
    }
  };

  // Changer la méthode de vérification
  const switchVerificationMethod = (method) => {
    setVerificationMethod(method);
    setOtp(['', '', '', '', '', '']);
    setError('');
    setSuccess('');
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  };

  // Styles CSS
  const styles = `
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    .otp-container {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #0a0e27 0%, #0a0f2a 100%);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', sans-serif;
      padding: 20px;
      position: relative;
    }

    /* Background */
    .otp-background {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      overflow: hidden;
      z-index: 0;
    }

    .otp-gradient-circle {
      position: absolute;
      width: 400px;
      height: 400px;
      border-radius: 50%;
      filter: blur(80px);
      opacity: 0.4;
      animation: float 20s ease-in-out infinite;
    }

    .otp-gradient-circle.top-right {
      background: linear-gradient(135deg, #6c63ff, #3f3d9e);
      top: -100px;
      right: -100px;
      animation-delay: -5s;
    }

    .otp-gradient-circle.bottom-left {
      background: linear-gradient(135deg, #00b4db, #0083b0);
      bottom: -100px;
      left: -100px;
      animation-delay: -15s;
    }

    @keyframes float {
      0%, 100% { transform: translate(0, 0) scale(1); }
      33% { transform: translate(30px, -30px) scale(1.1); }
      66% { transform: translate(-20px, 20px) scale(0.9); }
    }

    /* Carte principale */
    .otp-card {
      position: relative;
      z-index: 1;
      background: rgba(15, 25, 55, 0.7);
      backdrop-filter: blur(12px);
      border: 1px solid rgba(100, 150, 255, 0.2);
      border-radius: 24px;
      padding: 48px;
      max-width: 520px;
      width: 100%;
      box-shadow: 0 25px 45px rgba(0, 0, 0, 0.3), 0 0 20px rgba(0, 100, 255, 0.1);
      transition: all 0.3s ease;
    }

    .otp-card:hover {
      border-color: rgba(100, 150, 255, 0.4);
      box-shadow: 0 30px 55px rgba(0, 0, 0, 0.4), 0 0 30px rgba(0, 150, 255, 0.2);
    }

    /* Header */
    .otp-header {
      text-align: center;
      margin-bottom: 32px;
    }

    .otp-icon-wrapper {
      width: 70px;
      height: 70px;
      margin: 0 auto 16px;
      border-radius: 50%;
      background: linear-gradient(135deg, rgba(0, 180, 255, 0.2), rgba(108, 99, 255, 0.2));
      display: flex;
      align-items: center;
      justify-content: center;
      border: 2px solid rgba(0, 180, 255, 0.3);
    }

    .otp-icon-wrapper svg {
      font-size: 32px;
      color: #00b4ff;
    }

    .otp-title {
      font-size: 28px;
      font-weight: 700;
      color: #ffffff;
      margin-bottom: 8px;
    }

    .otp-subtitle {
      font-size: 15px;
      color: rgba(170, 200, 255, 0.7);
      line-height: 1.6;
    }

    /* Messages */
    .otp-message {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 12px 16px;
      border-radius: 12px;
      margin-bottom: 20px;
      animation: slideIn 0.3s ease;
    }

    .otp-message.error {
      background: rgba(255, 68, 68, 0.15);
      border: 1px solid rgba(255, 68, 68, 0.3);
    }

    .otp-message.success {
      background: rgba(68, 255, 68, 0.15);
      border: 1px solid rgba(68, 255, 68, 0.3);
    }

    .otp-message-icon {
      font-size: 18px;
    }

    .otp-message-text {
      color: rgba(255, 255, 255, 0.95);
      font-size: 14px;
      flex: 1;
    }

    @keyframes slideIn {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes shake {
      0%, 100% { transform: translateX(0); }
      25% { transform: translateX(-10px); }
      75% { transform: translateX(10px); }
    }

    /* Méthode de vérification */
    .method-selector {
      display: flex;
      gap: 12px;
      margin-bottom: 24px;
    }

    .method-btn {
      flex: 1;
      padding: 12px;
      border-radius: 12px;
      border: 2px solid rgba(100, 150, 255, 0.2);
      background: rgba(10, 20, 45, 0.4);
      color: rgba(170, 200, 255, 0.6);
      cursor: pointer;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      font-size: 14px;
      font-weight: 500;
    }

    .method-btn.active {
      border-color: rgba(0, 180, 255, 0.6);
      background: rgba(0, 180, 255, 0.15);
      color: #00b4ff;
    }

    .method-btn:hover:not(.active) {
      border-color: rgba(100, 150, 255, 0.4);
      color: rgba(255, 255, 255, 0.8);
    }

    .method-btn svg {
      font-size: 18px;
    }

    /* Champs OTP */
    .otp-inputs {
      display: flex;
      gap: 12px;
      justify-content: center;
      margin-bottom: 24px;
    }

    .otp-input {
      width: 52px;
      height: 60px;
      text-align: center;
      font-size: 24px;
      font-weight: 700;
      background: rgba(10, 20, 45, 0.6);
      border: 2px solid rgba(100, 150, 255, 0.2);
      border-radius: 12px;
      color: #ffffff;
      transition: all 0.3s ease;
      outline: none;
      font-family: inherit;
    }

    .otp-input:focus {
      border-color: rgba(0, 180, 255, 0.6);
      background: rgba(10, 20, 45, 0.8);
      box-shadow: 0 0 20px rgba(0, 180, 255, 0.2);
      transform: translateY(-2px);
    }

    .otp-input.has-value {
      border-color: rgba(0, 180, 255, 0.4);
      background: rgba(0, 180, 255, 0.1);
    }

    .otp-input.error-shake {
      animation: shake 0.5s ease;
      border-color: rgba(255, 68, 68, 0.6);
    }

    /* Info de destination */
    .otp-destination {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 12px;
      background: rgba(10, 20, 45, 0.3);
      border-radius: 10px;
      margin-bottom: 20px;
      color: rgba(170, 200, 255, 0.7);
      font-size: 14px;
    }

    .otp-destination strong {
      color: #ffffff;
    }

    .otp-destination svg {
      color: #00b4ff;
    }

    /* Compte à rebours */
    .otp-timer {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      margin-bottom: 16px;
      color: rgba(170, 200, 255, 0.6);
      font-size: 14px;
    }

    .otp-timer svg {
      font-size: 18px;
    }

    .otp-timer-count {
      font-weight: 700;
      color: #00b4ff;
      font-size: 18px;
    }

    /* Boutons */
    .otp-btn {
      width: 100%;
      padding: 14px;
      border-radius: 12px;
      border: none;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
    }

    .otp-btn-primary {
      background: linear-gradient(135deg, rgba(0, 100, 255, 0.8), rgba(0, 50, 150, 0.8));
      color: white;
      border: 1px solid rgba(0, 180, 255, 0.4);
      margin-bottom: 12px;
    }

    .otp-btn-primary:hover:not(:disabled) {
      background: linear-gradient(135deg, rgba(0, 120, 255, 0.9), rgba(0, 70, 180, 0.9));
      transform: translateY(-2px);
      box-shadow: 0 0 20px rgba(0, 150, 255, 0.3);
    }

    .otp-btn-secondary {
      background: rgba(255, 255, 255, 0.05);
      color: rgba(170, 200, 255, 0.8);
      border: 1px solid rgba(100, 150, 255, 0.2);
    }

    .otp-btn-secondary:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.1);
      color: #ffffff;
      transform: translateY(-2px);
    }

    .otp-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    /* Retour */
    .otp-back-link {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      margin-top: 16px;
      color: rgba(170, 200, 255, 0.5);
      text-decoration: none;
      font-size: 14px;
      transition: all 0.3s ease;
    }

    .otp-back-link:hover {
      color: #00b4ff;
    }

    /* Modal de succès */
    .success-modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.85);
      backdrop-filter: blur(12px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
      padding: 20px;
    }

    .success-modal {
      background: linear-gradient(145deg, #0a0e27, #0d1f3c);
      border: 2px solid rgba(0, 255, 100, 0.4);
      border-radius: 28px;
      padding: 48px;
      max-width: 450px;
      width: 100%;
      text-align: center;
      box-shadow: 0 40px 80px rgba(0, 0, 0, 0.6), 0 0 60px rgba(0, 255, 100, 0.15);
    }

    .success-icon-wrapper {
      width: 100px;
      height: 100px;
      margin: 0 auto 20px;
      border-radius: 50%;
      background: linear-gradient(135deg, #00ff88, #00cc66);
      display: flex;
      align-items: center;
      justify-content: center;
      animation: successPulse 2s ease-in-out infinite;
    }

    .success-icon-wrapper svg {
      font-size: 48px;
      color: white;
    }

    @keyframes successPulse {
      0%, 100% { transform: scale(1); box-shadow: 0 0 20px rgba(0, 255, 136, 0.3); }
      50% { transform: scale(1.05); box-shadow: 0 0 40px rgba(0, 255, 136, 0.5); }
    }

    .success-title {
      font-size: 28px;
      font-weight: 700;
      color: #00ff88;
      margin-bottom: 8px;
    }

    .success-subtitle {
      color: rgba(255, 255, 255, 0.7);
      font-size: 15px;
      line-height: 1.6;
    }

    .success-user {
      margin-top: 16px;
      padding: 16px;
      background: rgba(0, 255, 136, 0.08);
      border-radius: 12px;
      border: 1px solid rgba(0, 255, 136, 0.2);
    }

    .success-user-item {
      display: flex;
      align-items: center;
      gap: 12px;
      color: rgba(255, 255, 255, 0.8);
      padding: 6px 0;
      font-size: 14px;
    }

    .success-user-item svg {
      color: #00ff88;
    }

    /* Responsive */
    @media (max-width: 768px) {
      .otp-card {
        padding: 32px 24px;
        border-radius: 16px;
      }

      .otp-title {
        font-size: 24px;
      }

      .otp-input {
        width: 44px;
        height: 52px;
        font-size: 20px;
      }

      .otp-inputs {
        gap: 8px;
      }

      .method-btn {
        font-size: 13px;
        padding: 10px;
      }
    }

    @media (max-width: 480px) {
      .otp-input {
        width: 38px;
        height: 46px;
        font-size: 18px;
      }

      .otp-inputs {
        gap: 6px;
      }

      .otp-card {
        padding: 24px 16px;
      }

      .success-modal {
        padding: 32px 20px;
      }
    }
  `;

  return (
    <div className="otp-container">
      <style>{styles}</style>

      {/* Background */}
      <div className="otp-background">
        <div className="otp-gradient-circle top-right"></div>
        <div className="otp-gradient-circle bottom-left"></div>
      </div>

      {/* Carte principale */}
      <motion.div 
        className="otp-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Header */}
        <div className="otp-header">
          <div className="otp-icon-wrapper">
            <FiShield />
          </div>
          <h1 className="otp-title">Vérification OTP</h1>
          <p className="otp-subtitle">
            Un code de vérification a été envoyé à votre {verificationMethod === 'email' ? 'adresse email' : 'numéro de téléphone'}
          </p>
        </div>

        {/* Messages d'erreur/succès */}
        <AnimatePresence>
          {(error || success) && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className={`otp-message ${error ? 'error' : 'success'}`}
            >
              <span className="otp-message-icon">{error ? <FiX /> : <FiCheck />}</span>
              <span className="otp-message-text">{error || success}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Sélecteur de méthode */}
        <div className="method-selector">
          <button
            className={`method-btn ${verificationMethod === 'email' ? 'active' : ''}`}
            onClick={() => switchVerificationMethod('email')}
          >
            <FiMail />
            Email
          </button>
          <button
            className={`method-btn ${verificationMethod === 'sms' ? 'active' : ''}`}
            onClick={() => switchVerificationMethod('sms')}
          >
            <FiPhone />
            SMS
          </button>
        </div>

        {/* Destination */}
        <div className="otp-destination">
          {verificationMethod === 'email' ? (
            <>
              <FiMail />
              <span>Code envoyé à <strong>{email}</strong></span>
            </>
          ) : (
            <>
              <FiPhone />
              <span>Code envoyé à <strong>{tel}</strong></span>
            </>
          )}
        </div>

        {/* Champs OTP */}
        <div className="otp-inputs">
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={el => inputRefs.current[index] = el}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : undefined}
              className={`otp-input ${digit ? 'has-value' : ''} ${error ? 'error-shake' : ''}`}
              autoComplete="one-time-code"
              aria-label={`Code OTP chiffre ${index + 1}`}
            />
          ))}
        </div>

        {/* Timer */}
        <div className="otp-timer">
          <FiClock />
          <span>Renvoyer le code dans </span>
          <span className="otp-timer-count">{countdown}</span>
          <span> secondes</span>
        </div>

        {/* Boutons */}
        <button
          className="otp-btn otp-btn-primary"
          onClick={verifyOTP}
          disabled={loading || otp.join('').length !== 6}
        >
          {loading ? (
            'Vérification en cours...'
          ) : (
            <>
              <FiCheck />
              Vérifier
            </>
          )}
        </button>

        <button
          className="otp-btn otp-btn-secondary"
          onClick={sendOTP}
          disabled={loading || resendDisabled}
        >
          <FiRefreshCw />
          Renvoyer le code
        </button>

        {/* Lien retour */}
        <Link to="/register" className="otp-back-link">
          <FiArrowLeft />
          Retour à l'inscription
        </Link>
      </motion.div>

      {/* Modal de succès */}
      <AnimatePresence>
        {showSuccessModal && (
          <motion.div 
            className="success-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div 
              className="success-modal"
              initial={{ scale: 0.8, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 20 }}
              transition={{ type: "spring", damping: 20 }}
            >
              <div className="success-icon-wrapper">
                <FiCheck />
              </div>
              <h2 className="success-title">✅ Vérifié !</h2>
              <p className="success-subtitle">
                Votre compte a été vérifié avec succès. Vous allez être redirigé vers la page de connexion.
              </p>
              <div className="success-user">
                {userData.nom && (
                  <div className="success-user-item">
                    <FiUser />
                    <span>{userData.prenom} {userData.nom}</span>
                  </div>
                )}
                <div className="success-user-item">
                  <FiMail />
                  <span>{email}</span>
                </div>
                {tel && (
                  <div className="success-user-item">
                    <FiPhone />
                    <span>{tel}</span>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VerificationOTP;